import type { V2ConversationResponse } from "@/types/v2";
import type { V2ConversationMessage } from "./types";

export const MESSAGE_PREVIEW_DURATION = 10 * SECOND;

export const defaultResponseOverlayMinWidth = 200;
export const defaultResponseOverlayMaxWidth = Math.min(1000, window.innerWidth * 0.5);
export const defaultResponseOverlayWidth = Math.min(360, window.innerWidth);

export const useV2ResponseStore = defineStore("v2Response", () => {
  // Show chat bubble instead of full overlay by default
  const isResponseOverlayCollapsed = ref(true);
  const messages = ref<V2ConversationMessage[]>([]);
  const selectedSuggestion = ref("");
  const unreadCount = ref(0);
  const lastMessage = ref<V2ConversationMessage | null>(null);
  const isPreviewDismissed = ref(false);
  const lastPreviewUpdate = ref(0);
  const lastConversationId = ref("");
  const isNewConversation = ref(false);
  const responseOverlayWidth = useLocalStorage(
    "responseOverlayWidth",
    defaultResponseOverlayWidth
  );
  const responseOverlayMinWidth = ref(defaultResponseOverlayMinWidth);
  const responseOverlayMaxWidth = ref(defaultResponseOverlayMaxWidth);
  const timedOutConversations = ref<Record<string, number>>({});
  const processingConversations = ref<Record<string, number>>({});
  const [recentConversationsOpen, toggleRecentConversations] = useToggle();

  function hideResponseOverlay() {
    isResponseOverlayCollapsed.value = true;
  }

  function dismissPreview() {
    isPreviewDismissed.value = true;
  }

  function showResponseOverlay() {
    isResponseOverlayCollapsed.value = false;
    unreadCount.value = 0;
    dismissPreview();
  }

  function addMessage(
    _message: V2ConversationResponse["step"],
    role: "assistant" | "user"
  ) {
    const created = new Date().toISOString();
    const content = formatV2ResponseMessage(_message.statement);
    const id = getRandomId();
    const message = {
      content,
      created,
      id,
      role,
    };
    const length = messages.value.push(message);
    lastMessage.value = message;
    if (role === "assistant" && isResponseOverlayCollapsed.value) {
      unreadCount.value += 1;
      lastPreviewUpdate.value = Date.now();
      isPreviewDismissed.value = false;
    }
    return length - 1;
  }

  function removeMessage(index: number) {
    if (index < 0 || index >= messages.value.length) return;
    messages.value = messages.value.filter((_, i) => i !== index);
  }

  function startNewConversation() {
    messages.value = [];
    selectedSuggestion.value = "";
    unreadCount.value = 0;
    lastMessage.value = null;
    isPreviewDismissed.value = false;
    lastPreviewUpdate.value = 0;
    isNewConversation.value = true;
    lastConversationId.value = "";
    toggleRecentConversations(false);
  }

  function addTimedOutConversation(id: string) {
    timedOutConversations.value[id] = (timedOutConversations.value[id] || 0) + 1;
  }

  function isConversationTimedOut(id: string) {
    return !!timedOutConversations.value[id];
  }

  function dismissTimeoutConversation(id: string) {
    timedOutConversations.value[id] = (timedOutConversations.value[id] || 0) - 1;
    if (timedOutConversations.value[id] <= 0) {
      delete timedOutConversations.value[id];
    }
  }

  function addProcessingConversation(id: string) {
    processingConversations.value[id] = (processingConversations.value[id] || 0) + 1;
  }

  function dismissProcessingConversation(id: string) {
    processingConversations.value[id] = (processingConversations.value[id] || 0) - 1;
    if (processingConversations.value[id] <= 0) {
      delete processingConversations.value[id];
    }
  }

  function isConversationProcessing(id: string) {
    return !!processingConversations.value[id];
  }

  return {
    messages,
    unreadCount,
    selectedSuggestion,
    isResponseOverlayCollapsed,
    isPreviewDismissed,
    lastConversationId,
    dismissPreview,
    responseOverlayWidth,
    responseOverlayMinWidth,
    responseOverlayMaxWidth,
    lastPreviewUpdate,
    lastMessage,
    addMessage,
    hideResponseOverlay,
    showResponseOverlay,
    dismissTimeoutConversation,
    isConversationTimedOut,
    addTimedOutConversation,
    timedOutConversations,
    startNewConversation,
    isNewConversation,
    processingConversations,
    addProcessingConversation,
    dismissProcessingConversation,
    isConversationProcessing,
    recentConversationsOpen,
    toggleRecentConversations,
    removeMessage,
  };
});
